import clone from 'clone';
import * as React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Activity, CustomForm, CustomFormAnswer, CustomFormInputField, CustomFormReportData, fieldValidation, FileUploadCustomFormFieldData, isInputField, rng, UserToken } from '../../../amorg-common';
import { getActivity, updateActivity } from '../../../api/activityClientApi';
import { deleteCustomFormAnswer, deleteCustomFormReport, generateCustomFormTestAnswers, getCustomForm, getCustomFormAnswerList, getCustomFormByActivityId, getCustomFormReport, setCustomFormPublicLink, startCustomFormAnswer } from '../../../api/customFormClientApi';
import { getMaybeActiveUserOrRefresh, hasUserPermissions } from '../../../api/serverApiHelpers';
import { Button, ButtonProps } from '../../../designSystem/button/Button';
import { LinkButton } from '../../../designSystem/button/LinkButton';
import { Card } from '../../../designSystem/Card';
import { colors } from '../../../designSystem/colors/color';
import { DeleteConfirmationButton } from '../../../designSystem/compositeComponents/DeleteConfirmationButton';
import { EditableText } from '../../../designSystem/compositeComponents/EditableText';
import { Flex, FlexItem } from '../../../designSystem/compositeComponents/FlexHelpers';
import { LinkWithCopyButton } from '../../../designSystem/compositeComponents/LinkWithCopyButton';
import { FormField } from '../../../designSystem/formField/FormField';
import { Icon } from '../../../designSystem/icons/Icons';
import { StartEndDateFields } from '../../../designSystem/inputs/StartEndDateFields';
import { Label } from '../../../designSystem/Label';
import { ProtectedPageWrapper } from '../../../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { Link } from '../../../designSystem/Link';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { P, Small } from '../../../designSystem/typography/Body';
import { ActivityHeader } from '../../_components/activities/ActivityHeader';
import { CustomFormFilter, customFormHelper } from '../../../utils/customFormHelper';
import { toDateTimeString } from '../../../utils/date';
import { customFormFieldFactory } from '../_components/customFormFieldFactory';
import { CustomFormTabHeader } from './_components/CustomFormTabHeader';
import { CustomFormFiltersPanel } from './report/_components/CustomFormFiltersPanel';


export function CustomFormPage (): JSX.Element
{
    const [redirectOverview, setRedirectOverview] = React.useState(false);
    const [redirectNewAnswerId, setRedirectNewAnswerId] = React.useState('');

    const { activityId } = useParams<{ activityId: string; }>();

    const [state, setState] = React.useState<{
        isLoading: boolean,
        user: UserToken | null,
        form: CustomForm | null,
        activity:Activity | null,
        report: CustomFormReportData | null,
        answers: CustomFormAnswer[],
        filters: CustomFormFilter[] | null;
    }>(
        { isLoading: true, user: null, form: null, report: null, answers: [], filters: null, 
            activity:{
                id: '',
                title: '',
                description: '',
                start: new Date(),
                end: new Date(),
                location: '',
                activityType: 'CustomForm',
                invitationType:'InviteOnly',
                workgroupId:'',
                workgroupName:'',
                owner:null,
                participants:[],
                agendaItems:[],
            } });

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            const user = (await getMaybeActiveUserOrRefresh()).tokenUser;
            const form = await getCustomFormByActivityId(activityId || '');
            const activity = await getActivity(form?.activityId || '');
            const answers = await getCustomFormAnswerList(form?.id || '');
            const report = await getCustomFormReport(form?.id || '');
            if(form && report){
                const filters = customFormHelper.buildFilters(form, report);
                setState({ isLoading: false, user, form, activity, report, answers, filters });
            }
        };
        getData();
    }, []);

    async function onClickCreateNew ()
    {
        if(!state.form)
            return;

        setState({ ...state, isLoading: true });
        const newAnswerId = rng.getRandomId();
        await startCustomFormAnswer(state.form.id, newAnswerId);
        const answers = await getCustomFormAnswerList(state.form.id);
        setState({ ...state, isLoading: false, answers });
        setRedirectNewAnswerId(newAnswerId)
    }

    async function handleClickBuildTestAnswers ()
    {
        if(!state.form)
            return;

        setState({ ...state, isLoading: true });
        await generateCustomFormTestAnswers(state.form.id, 10);
        setState({
            ...state,
            answers: await getCustomFormAnswerList(state.form.id),
            isLoading: false
        });
    }

    function handleFilterChange (fieldId: string, value: string)
    {
        if(!state.filters)
            return;

        const newFilters = clone(state.filters);
        const filter = newFilters.find(f => f.fieldId === fieldId);

        if (filter) {
            filter.selectedValue = value;
        }

        setState({ ...state, filters: newFilters });
    }

    async function handleClickDeleteAnswer (answerId: string)
    {
        if(!state.form)
            return;

        setState({ ...state, isLoading: true });
        await deleteCustomFormAnswer(answerId);
        const answers = await getCustomFormAnswerList(state.form.id);
        const report = await getCustomFormReport(state.form.id);
        if(!report){
            throw new Error('Unable to load report');
        }
        const filters = customFormHelper.buildFilters(state.form, report);
        setState({ ...state, isLoading: false, report, answers, filters });
    }

    async function handleLinkUpdated ()
    {
        if(!state.form)
            return;

        setState({ ...state, isLoading: true });
        const form = await getCustomForm(state.form.id);
        setState({ ...state, form });
    }

    async function handleSaveTitle (value:string, isValid:boolean)
    {
        if (!isValid || !state.activity)
            return;

        const a = state.activity;
        await updateActivity(a.id, value, a.description, a.start, a.end, a.location);
        const activity = await getActivity(state.activity.id);
        setState({ ...state, activity });
    }

    async function handleUpdateDates(start:Date, end:Date)
    {
        if(!state.activity)
            return;

        const a = state.activity;
        const result = await updateActivity(a.id, a.title, a.description, start, end, a.location);
        if (result.isSuccess)
        {
            const activity = await getActivity(state.activity.id);
            setState({ ...state, activity });
        }
    }

    let filteredAnswers: CustomFormAnswer[] = [];
    if (state.answers.length > 0 && state.filters && state.filters.length > 0)
    {
        filteredAnswers = customFormHelper.applyAnswerFilters(
            state.answers, state.filters
        );
    }

    const inputFields = state.form?.fields.filter(f => isInputField(f));
    const canEditLink = state.user ? hasUserPermissions(state.user, ['EditCustomForms']) : false;

    return (

        <ProtectedPageWrapper requiredPermissions={['ViewCustomFormAnswerList']} selectedMenuItemKey={state.form?.category || ''}>
            
            {state.form !== null && state.activity &&

                <SingleColumnLayout
                    noDivider
                    contentContainer={
                        <>
                            {redirectOverview &&
                                <Navigate to={`/customForms/${state.form.id}/report`} />
                            }

                            {redirectNewAnswerId &&
                                <Navigate to={`/customforms/${state.form.id}/answers/${redirectNewAnswerId}`} />
                            }

                            <LoadingPageOverlay loading={state.isLoading} />

                            <CustomFormTabHeader selectedTab='answers'
                                onClick={(tabName) =>
                                {
                                    if (tabName === 'overview')
                                        setRedirectOverview(true);
                                }}
                            />

                            <ActivityHeader
                                activity={state.activity}
                                deleteConfirmationTextInModal='Er du sikker på at du vil slette undersøgelsen?'
                                additionalToolBarButtons={[
                                    {
                                        icon:'interface_actions_send',
                                        tooltip:'Opret 10 testbesvarelser',
                                        variant:'text',
                                        onClick:handleClickBuildTestAnswers
                                    },
                                    {
                                        icon:'interface_actions_synchronize',
                                        tooltip:'Gendan rapport',
                                        variant:'text',
                                        onClick:async () =>
                                        {
                                            if(!state.form || !state.report)
                                                return;

                                            setState({ ...state, isLoading: true });
                                            await deleteCustomFormReport(state.form.id);
                                            setState({ ...state, isLoading: false });
                                        }
                                    }
                                ]} />

                            <Flex flexDirection='row' gap={16} >
                                <FlexItem width='100%'>
                                    <Label>Undersøgelse</Label>
                                    
                                    <EditableText
                                        styleMode='H2'
                                        ariaLabel={'Rediger undersøgelsens titel'} 
                                        value={state.activity?.title || ''}
                                        validator={(value) => {
                                            if(!fieldValidation.activity.title(value))
                                                return { valid: false, errorMessage: 'Feltet skal udfyldes. Maks. 100 tegn.' };
                                            return { valid: true };
                                        }}
                                        onSave={handleSaveTitle} placeholder={''} />
                                </FlexItem>
                                
                                <Flex flexDirection="row">
                                    <FormField 
                                        label={"Periode"} 
                                        inputComponent={
                                            <StartEndDateFields 
                                                start={state.activity?.start || new Date()} end={state.activity?.end || new Date()} 
                                                onChanged={handleUpdateDates} />
                                        } />
                                </Flex>
                            </Flex>


                            <Flex flexDirection='row' gap={16} justifyContent='space-between' alignItems="flex-start">
                                <FormLinkPanel form={state.form} canEdit={canEditLink} onUpdated={handleLinkUpdated} />

                                {state.answers && state.report && state.filters &&
                                
                                    <CustomFormFiltersPanel form={state.form} report={state.report} filter={state.filters}
                                        onFilterChange={handleFilterChange}
                                        buttons={[
                                            {
                                                variant: 'filled',
                                                text: "Opret besvarelse",
                                                icon: "add_circle",
                                                onClick: onClickCreateNew
                                            }]} />
                                            
                                }
                            </Flex>

                            {state.answers.length === 0 &&
                                <div>Ingen besvarelser oprettet</div>
                            }

                            {state.answers.length > 0 && filteredAnswers.length === 0 &&
                                <div>Ingen besvarelser med det valgte filter</div>
                            }

                            {state.form && state.answers &&
                                <>
                                    {filteredAnswers.map((answer) => 
                                    {
                                        if(!state.form)
                                            return;

                                        const publicUrl = `${window.location.protocol}//${window.location.host}/customforms/public/${state.user?.siteId}/${answer.id}`;
                                        const videoFields = state.form.fields.filter(f => isInputField(f) && f.type === 'FileUpload');
                                        const videoTypeSelections = answer.selections.filter(s => videoFields.find(f => f.fieldId === s.fieldId));
                                        const videoCount = videoTypeSelections.filter(s => 
                                        {
                                            if(!s.value) return false;
                                            try{
                                                const data = JSON.parse(s.value) as FileUploadCustomFormFieldData;
                                                return data.status.stage === 'complete';
                                            }
                                            catch(e)
                                            {
                                                return false;
                                            }
                                        }).length;
                                        const videoContentColor = videoCount===0 ? colors.text.gray.light : colors.text.normal;
                                        return (
                                            <Card key={answer.id} variant="outlined">
                                                <Flex flexDirection='row' gap={16} justifyContent='space-between'>

                                                    <FlexItem flexGrow='1' style={{width:'50%'}}>
                                                        <Flex flexDirection='column' gap={8} alignItems='flex-start'>
                                                            {inputFields && inputFields.filter(f => f.isPrimaryField).map((field: CustomFormInputField) =>
                                                            {
                                                                const answerSelection = answer.selections.find(s => s.fieldId === field.fieldId);
                                                                
                                                                return (
                                                                    <Flex flexDirection='column' key={field.fieldId} alignItems='flex-start'>
                                                                        <Small>{field.label}</Small>
                                                                        <P>{answerSelection
                                                                            ? customFormFieldFactory.getAnswerFieldText(field, answerSelection)
                                                                            : ''}
                                                                        </P>
                                                                    </Flex>
                                                                );
                                                            })}
                                                        </Flex>
                                                    </FlexItem>


                                                    <Flex flexDirection='column' gap={8} justifyContent='space-between' style={{width:'50%'}}>
                                                        <Flex flexDirection='column' gap={8}>
                                                            <Flex gap={16}>
                                                                <TimeLabel text={`oprettet ${toDateTimeString(answer.started)}`} />
                                                                <TimeLabel text={`sidst redigeret ${toDateTimeString(answer.lastEdited)}`} />
                                                            </Flex>
                                                            <Flex gap={8}>
                                                                <Icon name="smart_display" color={videoContentColor} /> 
                                                                <span style={{color:videoContentColor}}>{videoCount}</span>
                                                            </Flex>
                                                        </Flex>

                                                        <Flex flexDirection="column" gap={8} alignItems='flex-end'>

                                                            <LinkWithCopyButton url={publicUrl} />

                                                            <Flex gap={16} justifyContent='flex-end'>
                                                                <DeleteConfirmationButton buttonText='Slet besvarelsen' confirmationHeading='Slet besvarelse' requireKey={false}
                                                                    confirmationText='Er du sikker på at du vil slette besvarelsen?'
                                                                    onConfirmDelete={() => handleClickDeleteAnswer(answer.id)} />

                                                                <LinkButton href={`/customForms/${state.form.id}/answers/${answer.id}`}
                                                                    variant='light'
                                                                    text='Rediger'
                                                                    icon='interface_actions_edit'
                                                                />
                                                            </Flex>
                                                        </Flex>
                                                    </Flex>


                                                </Flex>
                                            </Card>);
                                    }
                                    )}
                                </>
                            }
                        </>
                    } 
                />
            }

        </ProtectedPageWrapper>
    );
}

type TimeLabelProps = {
    text: string;
};
function TimeLabel (props: TimeLabelProps): JSX.Element
{
    return (
        <Flex gap={4} alignItems='center'>
            <Icon name='clock' size='x-small' /><Small>{props.text}</Small>
        </Flex>
    );
}


type FormLinkPanelProps = {
    canEdit: boolean,
    form: CustomForm,
    onUpdated: () => void;
};
function FormLinkPanel (props: FormLinkPanelProps): JSX.Element
{
    const [siteId, setSiteId] = React.useState<string>('');

    React.useEffect(() =>
    {
        const getActiveUser = async () =>
        {
            const user = await getMaybeActiveUserOrRefresh([]);
            if(!user.tokenUser)
                return;
            
            setSiteId(user.tokenUser.siteId);
        };
        getActiveUser();

    }, []);

    async function handleCreateLink ()
    {
        await setCustomFormPublicLink(props.form.id, 'create');
        props.onUpdated();
    }

    async function handleDeleteLink ()
    {
        await setCustomFormPublicLink(props.form.id, 'remove');
        props.onUpdated();
    }

    const url = `${window.location.protocol}//${window.location.host}/customForms/public/${props.form?.publicLinkKey || '#'}@${siteId}`;

    return (
        <div>
            <Label>Link til oprettelse af besvarelser</Label>
            {!props.form?.publicLinkKey &&
                <Flex flexDirection="row" alignItems='center' gap={16}>
                    <div>Der er ikke oprettet et link til undersøgelsen</div>
                    {props.canEdit &&
                        <Button variant="outlined" text="Opret link" onClick={handleCreateLink} />
                    }
                </Flex>
            }

            {props.form?.publicLinkKey &&
                <LinkWithButtons
                    url={url}
                    disabled={!props.form?.publicLinkKey}
                    extraButtons={
                        props.canEdit
                            ? [{
                                icon: "interface_actions_delete",
                                variant: "destructive-text",
                                onClick: handleDeleteLink
                            }]
                            : []
                        } 
                />
            }
        </div>
    );
}

type LinkWithButtonsProps = {
    url:string,
    disabled?:boolean,
    extraButtons?:ButtonProps[]
}
function LinkWithButtons(props:LinkWithButtonsProps):JSX.Element
{
    return (
        <Flex flexDirection="row" alignItems='center' gap={16}>
            <LinkWithCopyButton
                disabled={props.disabled}
                url={props.url}
                extraButtons={props.extraButtons} />
        </Flex>
    );
}